import { forwardRef, useMemo } from 'react';
import { PLUTCHIK_EMOTION_COLORS, contrastTextColor } from '@citizen-walk/shared-types';
import type { PostMinimal } from '../posts';
import { INSIGHT_COLOR_PALETTE } from './insightMapHelpers';
import type { IdeaMinimal } from './useEcwIdeas';
import type { InsightMinimal } from './useEcwInsights';

/**
 * Phase B+2-1 — 処方箋カード PDF 出力テンプレート (旧
 * `old/ECW_framework_v3.2_AIJ_Paper/src/components/ReportPdfTemplate.tsx` 1:1 移植)。
 *
 * 役割:
 *   - 詳細 modal が開いた間だけ DOM 上にレンダされる「隠し DOM」。
 *   - html2canvas が画像化することを前提とした **A4 縦 (794px)** の完全コピー版を
 *     全 section に `className="pdf-section"` を付けて並べる。
 *   - `generateIdeaPdf` が `pdf-section` ごとに canvas 化し、jsPDF で A4 縦に積み上げる。
 *
 * inline style の例外:
 *   - 本リポジトリのデザイン規律では原則 inline style 禁止 (CLAUDE.md 「デザインシステム」
 *     section)。ただし PDF 出力テンプレートは:
 *       (a) 旧コードが全 style を inline で書いており UX を 1:1 で踏襲したい
 *       (b) html2canvas は computed style を採取するため、Tailwind preprocessor が
 *           class -> CSS 変換するタイミング差で「reset CSS が当たって枠線が消える」
 *           等の事故を回避するために、出力モジュールでは inline style が事実上の
 *           デファクト・スタンダードである
 *     という 2 点から **例外的に inline style を許容** する。class は付けず、style 属性のみ。
 *
 * 表示構造 (旧 ReportPdfTemplate を v2 型へ写像):
 *   1. ヘッダー: emotionTag + categoryLabel chip + title + 作成者 / チーム / 日付
 *   2. キャッチ画像 + 関連市民データの地図 (横並び)
 *   3. アクションと効能 (`actionIdea` 生テキスト、whiteSpace:pre-wrap)
 *   4. 処方箋検討時の AI 対話記録 (llmInsightSummary, llmActionQuestions, llmEffectQuestions,
 *      actionIdea を `【効能】` で split した処方 / 効能)
 *   5. ベースとした気づき一覧 (各 insight の symptoms/diagnosis、紐づく post 写真 grid、
 *      llmAnalysis / llmResolutionQuestions / llmDiagnosisQuestions)
 *   6. フッター "Enabling City Walk! 処方箋レポート"
 *
 * 隠し DOM 化は呼出側 (IdeaDetailModal) で wrapper div に
 * `style={{ position:'absolute', top:0, left:0, width:'794px', opacity:0, zIndex:-9999,
 *           pointerEvents:'none' }}` を当てる。
 */

export interface ReportPdfTemplateProps {
  idea: IdeaMinimal;
  posts: PostMinimal[];
  insights: InsightMinimal[];
  /** キャッチ画像 (referenceImageBase64 or 先頭 post の download URL、空文字なら No Image)。 */
  catchImageUrl: string;
  /** 地図 dataURL (生成側で html2canvas キャプチャ済、空文字なら地図 section "No Map")。 */
  mapImageUrl: string;
}

/**
 * 感情タグの配色は「プルチックの感情の輪」準拠の共通定数 (PLUTCHIK_EMOTION_COLORS) を使う。
 * M2 感情マスタ (`ecw-emotion`) の rows[].color / colorMap も同じ値で初期化されているため、
 * 分析画面のチップ・地図ピンと PDF レポートで同じ感情が同じ色になる。
 */
const EMOTION_COLORS: Record<string, string> = PLUTCHIK_EMOTION_COLORS;

function emotionColor(tag: string | undefined): string {
  if (!tag) return '#888';
  return EMOTION_COLORS[tag] ?? '#888';
}

function formatDate(d: IdeaMinimal['createdAt']): string {
  if (!d) return '';
  try {
    const date = d.toDate();
    return date.toLocaleDateString();
  } catch {
    return '';
  }
}

export const ReportPdfTemplate = forwardRef<HTMLDivElement, ReportPdfTemplateProps>(
  function ReportPdfTemplate({ idea, posts, insights, catchImageUrl, mapImageUrl }, ref) {
    const relatedInsights = useMemo(
      () => insights.filter((i) => idea.insightIds?.includes(i.id)),
      [insights, idea.insightIds],
    );

    const getInsightColor = (id: string): string => {
      const index = relatedInsights.findIndex((i) => i.id === id);
      const fallback = INSIGHT_COLOR_PALETTE[0];
      if (index < 0) return fallback;
      const c = INSIGHT_COLOR_PALETTE[index % INSIGHT_COLOR_PALETTE.length];
      return c ?? fallback;
    };

    // actionIdea を 【処方・アクション】 / 【効能】 で split (旧仕様)
    const { prescriptionText, effectText } = useMemo(() => {
      const text = idea.actionIdea || '';
      if (!text) return { prescriptionText: '', effectText: '' };
      const parts = text.split('【効能】');
      const head = parts[0] ?? '';
      const tail = parts.length > 1 ? (parts[1] ?? '').trim() : '';
      const prescription = head.replace('【処方・アクション】', '').trim();
      return { prescriptionText: prescription, effectText: tail };
    }, [idea.actionIdea]);

    return (
      <div
        ref={ref}
        id={`pdf-template-${idea.id}`}
        style={{
          width: '794px', // A4 Portrait width at 96 DPI
          background: '#ffffff',
          fontFamily:
            '"Helvetica Neue", Arial, "Hiragino Kaku Gothic ProN", "Hiragino Sans", Meiryo, sans-serif',
          color: '#333',
          lineHeight: 1.6,
          boxSizing: 'border-box',
          position: 'relative',
        }}
      >
        {/* 1. ヘッダー */}
        <div
          className="pdf-section"
          style={{
            width: '794px',
            boxSizing: 'border-box',
            background: '#fff',
            padding: '40px 40px 0 40px',
          }}
        >
          <div
            style={{
              borderBottom: '2px solid #2c3e50',
              paddingBottom: '20px',
              marginBottom: '30px',
            }}
          >
            <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
              {idea.emotionTag && (
                <span
                  style={{
                    background: emotionColor(idea.emotionTag),
                    // 黄 (喜び) / 水色 (驚き) 等の明るい色では白文字が読めないため輝度で切替。
                    color: contrastTextColor(emotionColor(idea.emotionTag)),
                    padding: '4px 12px',
                    borderRadius: '12px',
                    fontSize: '14px',
                    fontWeight: 'bold',
                  }}
                >
                  {idea.emotionTag}
                </span>
              )}
              {idea.categoryLabel && (
                <span
                  style={{
                    background: '#555',
                    color: '#fff',
                    padding: '4px 12px',
                    borderRadius: '12px',
                    fontSize: '14px',
                    fontWeight: 'bold',
                  }}
                >
                  {idea.categoryLabel}
                </span>
              )}
            </div>
            <h1
              style={{
                fontSize: '32px',
                margin: '0 0 16px 0',
                color: '#2c3e50',
                lineHeight: 1.4,
              }}
            >
              {idea.title || '(タイトルなし)'}
            </h1>
            <div
              style={{
                display: 'flex',
                gap: '24px',
                fontSize: '14px',
                color: '#666',
                flexWrap: 'wrap',
              }}
            >
              <div>
                <strong>作成者:</strong> {idea.nickname || '匿名'}
              </div>
              {idea.team && (
                <div>
                  <strong>チーム:</strong> {idea.team}
                </div>
              )}
              {idea.createdAt && (
                <div>
                  <strong>作成日:</strong> {formatDate(idea.createdAt)}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* 2. 画像と地図 */}
        <div
          className="pdf-section"
          style={{
            width: '794px',
            boxSizing: 'border-box',
            background: '#fff',
            padding: '0 40px',
            display: 'flex',
            gap: '20px',
            marginBottom: '30px',
          }}
        >
          <div style={{ flex: 1 }}>
            <h3
              style={{
                fontSize: '16px',
                color: '#2c3e50',
                marginBottom: '8px',
                borderBottom: '1px solid #ddd',
                paddingBottom: '4px',
              }}
            >
              キャッチ画像
            </h3>
            <div
              style={{
                height: '260px',
                borderRadius: '8px',
                border: '1px solid #eee',
                background: '#f5f5f5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
              }}
            >
              {catchImageUrl ? (
                <img
                  src={catchImageUrl}
                  alt="キャッチ画像"
                  loading="eager"
                  crossOrigin="anonymous"
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    width: 'auto',
                    height: 'auto',
                    display: 'block',
                  }}
                />
              ) : (
                <div style={{ color: '#aaa' }}>No Image</div>
              )}
            </div>
          </div>
          <div style={{ flex: 1 }}>
            <h3
              style={{
                fontSize: '16px',
                color: '#2c3e50',
                marginBottom: '8px',
                borderBottom: '1px solid #ddd',
                paddingBottom: '4px',
              }}
            >
              関連する市民データの位置
            </h3>
            <div
              style={{
                height: '260px',
                borderRadius: '8px',
                border: '1px solid #eee',
                background: '#f5f5f5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
              }}
            >
              {mapImageUrl ? (
                <img
                  src={mapImageUrl}
                  alt="地図"
                  loading="eager"
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    width: 'auto',
                    height: 'auto',
                    display: 'block',
                  }}
                />
              ) : (
                <div style={{ color: '#aaa' }}>No Map</div>
              )}
            </div>
          </div>
        </div>

        {/* 3. アクションと効能 */}
        <div
          className="pdf-section"
          style={{
            width: '794px',
            boxSizing: 'border-box',
            background: '#fff',
            padding: '0 40px',
            marginBottom: '30px',
          }}
        >
          <h2
            style={{
              fontSize: '20px',
              color: '#2c3e50',
              borderLeft: '5px solid #00B4D8',
              paddingLeft: '12px',
              marginBottom: '16px',
            }}
          >
            📝 アクションと効能
          </h2>
          <div
            style={{
              background: '#f9fafb',
              padding: '20px',
              borderRadius: '8px',
              border: '1px solid #e2e8f0',
              whiteSpace: 'pre-wrap',
              fontSize: '16px',
            }}
          >
            {idea.actionIdea || '(未記入)'}
          </div>
        </div>

        {/* 4. 処方箋検討時の AI 対話記録 */}
        <div
          className="pdf-section"
          style={{
            width: '794px',
            boxSizing: 'border-box',
            background: '#fff',
            padding: '0 40px',
            marginBottom: '40px',
          }}
        >
          <h2
            style={{
              fontSize: '20px',
              color: '#2c3e50',
              borderLeft: '5px solid #00B4D8',
              paddingLeft: '12px',
              marginBottom: '16px',
            }}
          >
            🤖 処方箋検討時の AI 対話記録
          </h2>
          {idea.llmInsightSummary ||
          (idea.llmActionQuestions && idea.llmActionQuestions.length > 0) ||
          (idea.llmEffectQuestions && idea.llmEffectQuestions.length > 0) ||
          prescriptionText ||
          effectText ? (
            <div
              style={{
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  background: '#fff8e1',
                  padding: '12px 16px',
                  borderBottom: '1px solid #e0e0e0',
                }}
              >
                <strong style={{ color: '#d35400', display: 'block', marginBottom: '4px' }}>
                  💡 選択した気づきの概要
                </strong>
                <div style={{ fontSize: '14px', whiteSpace: 'pre-wrap' }}>
                  {idea.llmInsightSummary || idea.observations || '(記録なし)'}
                </div>
              </div>
              {idea.llmActionQuestions && idea.llmActionQuestions.length > 0 && (
                <div style={{ padding: '12px 16px', borderBottom: '1px solid #e0e0e0' }}>
                  <strong style={{ color: '#2980b9', display: 'block', marginBottom: '4px' }}>
                    STEP 4: AI からの問いかけ (処方について)
                  </strong>
                  <ul style={{ margin: '4px 0', paddingLeft: '20px', fontSize: '14px' }}>
                    {idea.llmActionQuestions.map((q, i) => (
                      <li key={i}>{q}</li>
                    ))}
                  </ul>
                </div>
              )}
              <div
                style={{
                  background: '#fff8e1',
                  padding: '12px 16px',
                  borderBottom: '1px solid #e0e0e0',
                }}
              >
                <strong style={{ color: '#d35400', display: 'block', marginBottom: '4px' }}>
                  ✍️ 参加者の回答 (処方・アクション)
                </strong>
                <div style={{ fontSize: '14px', whiteSpace: 'pre-wrap' }}>
                  {prescriptionText || '(未記入)'}
                </div>
              </div>
              {idea.llmEffectQuestions && idea.llmEffectQuestions.length > 0 && (
                <div style={{ padding: '12px 16px', borderBottom: '1px solid #e0e0e0' }}>
                  <strong style={{ color: '#2980b9', display: 'block', marginBottom: '4px' }}>
                    STEP 5: AI からの問いかけ (効能について)
                  </strong>
                  <ul style={{ margin: '4px 0', paddingLeft: '20px', fontSize: '14px' }}>
                    {idea.llmEffectQuestions.map((q, i) => (
                      <li key={i}>{q}</li>
                    ))}
                  </ul>
                </div>
              )}
              <div style={{ background: '#fff8e1', padding: '12px 16px' }}>
                <strong style={{ color: '#d35400', display: 'block', marginBottom: '4px' }}>
                  ✍️ 参加者の回答 (効能)
                </strong>
                <div style={{ fontSize: '14px', whiteSpace: 'pre-wrap' }}>
                  {effectText || '(未記入)'}
                </div>
              </div>
            </div>
          ) : (
            <div style={{ color: '#888', fontSize: '14px', fontStyle: 'italic' }}>
              対話記録がありません。
            </div>
          )}
        </div>

        {/* 5. ベースとした気づき一覧 */}
        <div>
          <div
            className="pdf-section"
            style={{
              width: '794px',
              boxSizing: 'border-box',
              background: '#fff',
              padding: '0 40px',
              marginBottom: '24px',
            }}
          >
            <h2
              style={{
                fontSize: '20px',
                color: '#2c3e50',
                borderLeft: '5px solid #00B4D8',
                paddingLeft: '12px',
                margin: 0,
              }}
            >
              💡 ベースとした気づき一覧
            </h2>
          </div>
          {relatedInsights.map((ins, idx) => {
            const color = getInsightColor(ins.id);
            const insPosts = posts.filter((p) => ins.selectedPostIds.includes(p.id));

            return (
              <div
                key={ins.id}
                className="pdf-section"
                style={{
                  width: '794px',
                  boxSizing: 'border-box',
                  background: '#fff',
                  padding: '0 40px',
                  marginBottom: '40px',
                }}
              >
                <div
                  style={{
                    border: `2px solid ${color}`,
                    borderRadius: '12px',
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      background: color,
                      color: '#fff',
                      padding: '12px 20px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <h3 style={{ margin: 0, fontSize: '18px' }}>
                      気づき {idx + 1}: {ins.title || '(タイトルなし)'}
                    </h3>
                    <div
                      style={{
                        fontSize: '12px',
                        display: 'flex',
                        gap: '16px',
                        fontWeight: 'bold',
                      }}
                    >
                      <span>投稿者: {ins.nickname || '匿名'}</span>
                      {ins.team && <span>チーム: {ins.team}</span>}
                    </div>
                  </div>

                  <div style={{ padding: '20px', background: '#fff' }}>
                    {/* 症状と診断 */}
                    <div style={{ display: 'flex', gap: '20px', marginBottom: '20px' }}>
                      <div
                        style={{
                          flex: 1,
                          background: '#f8fafc',
                          padding: '16px',
                          borderRadius: '8px',
                          border: '1px solid #e2e8f0',
                        }}
                      >
                        <strong
                          style={{
                            display: 'block',
                            color: '#475569',
                            marginBottom: '8px',
                          }}
                        >
                          症状 (起きていること)
                        </strong>
                        <div style={{ fontSize: '14px', whiteSpace: 'pre-wrap' }}>
                          {ins.observations || '(未記入)'}
                        </div>
                      </div>
                      <div
                        style={{
                          flex: 1,
                          background: '#f8fafc',
                          padding: '16px',
                          borderRadius: '8px',
                          border: '1px solid #e2e8f0',
                        }}
                      >
                        <strong
                          style={{
                            display: 'block',
                            color: '#475569',
                            marginBottom: '8px',
                          }}
                        >
                          診断 (その理由・背景)
                        </strong>
                        <div style={{ fontSize: '14px', whiteSpace: 'pre-wrap' }}>
                          {ins.diagnosis || '(未記入)'}
                        </div>
                      </div>
                    </div>

                    {/* ベースとなった市民データ */}
                    {insPosts.length > 0 && (
                      <div style={{ marginBottom: '20px' }}>
                        <strong
                          style={{
                            display: 'block',
                            color: '#333',
                            marginBottom: '12px',
                            fontSize: '15px',
                          }}
                        >
                          📋 ベースとなった市民データ
                        </strong>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                          {insPosts.map((p, pIdx) => (
                            <div
                              key={p.id}
                              style={{
                                width: '120px',
                                border: '1px solid #ddd',
                                borderRadius: '6px',
                                overflow: 'hidden',
                              }}
                            >
                              <div
                                style={{
                                  width: '100%',
                                  height: '80px',
                                  background: '#f1f3f4',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                {p.photoThumbPath ? (
                                  // photo は generateIdeaPdf 側で download URL に置換済 (data-photo-path 経由)
                                  <img
                                    data-photo-path={p.photoThumbPath}
                                    alt={p.title || '投稿写真'}
                                    loading="eager"
                                    crossOrigin="anonymous"
                                    style={{
                                      maxWidth: '100%',
                                      maxHeight: '100%',
                                      width: 'auto',
                                      height: 'auto',
                                      display: 'block',
                                    }}
                                  />
                                ) : (
                                  <div style={{ color: '#aaa', fontSize: '11px' }}>No Photo</div>
                                )}
                              </div>
                              <div
                                style={{
                                  padding: '6px',
                                  fontSize: '10px',
                                  background: '#fff',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                }}
                              >
                                {pIdx + 1}. {p.title || '(無題)'}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* 気づき検討時の AI 対話 */}
                    {(ins.llmAnalysis ||
                      (ins.llmResolutionQuestions && ins.llmResolutionQuestions.length > 0) ||
                      (ins.llmDiagnosisQuestions && ins.llmDiagnosisQuestions.length > 0)) && (
                      <div>
                        <strong
                          style={{
                            display: 'block',
                            color: '#333',
                            marginBottom: '12px',
                            fontSize: '15px',
                          }}
                        >
                          🤖 気づきの検討時の AI との対話
                        </strong>
                        <div
                          style={{
                            fontSize: '13px',
                            background: '#fafafa',
                            border: '1px solid #eee',
                            borderRadius: '6px',
                            padding: '16px',
                          }}
                        >
                          {ins.llmAnalysis && (
                            <div style={{ marginBottom: '12px' }}>
                              <strong style={{ color: '#2980b9' }}>STEP 1: AI からの所見</strong>
                              <div style={{ marginTop: '4px', whiteSpace: 'pre-wrap' }}>
                                {ins.llmAnalysis}
                              </div>
                            </div>
                          )}
                          {ins.llmResolutionQuestions && ins.llmResolutionQuestions.length > 0 && (
                            <div style={{ marginBottom: '12px' }}>
                              <strong style={{ color: '#2980b9' }}>
                                STEP 2: AI からの問いかけ
                              </strong>
                              <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
                                {ins.llmResolutionQuestions.map((q, qi) => (
                                  <li key={qi}>{q}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {ins.llmDiagnosisQuestions && ins.llmDiagnosisQuestions.length > 0 && (
                            <div style={{ marginBottom: '12px' }}>
                              <strong style={{ color: '#2980b9' }}>
                                STEP 3: AI からの問いかけ
                              </strong>
                              <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
                                {ins.llmDiagnosisQuestions.map((q, qi) => (
                                  <li key={qi}>{q}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
          {relatedInsights.length === 0 && (
            <div
              className="pdf-section"
              style={{
                color: '#888',
                fontStyle: 'italic',
                padding: '0 40px',
                width: '794px',
                boxSizing: 'border-box',
                background: '#fff',
                marginBottom: '24px',
              }}
            >
              ベースとした気づきはありません。
            </div>
          )}
        </div>

        <div
          className="pdf-section"
          style={{
            width: '794px',
            boxSizing: 'border-box',
            background: '#fff',
            padding: '0 40px 40px 40px',
          }}
        >
          <div
            style={{
              textAlign: 'center',
              paddingTop: '20px',
              borderTop: '1px solid #eee',
              color: '#999',
              fontSize: '12px',
            }}
          >
            Enabling City Walk! 処方箋レポート
          </div>
        </div>
      </div>
    );
  },
);
